import { useEffect, useState } from "react";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Building2,
  Search,
  Loader2,
  ExternalLink,
  CheckCircle2,
  Clock,
  FileText,
  Send,
  Trash2,
  Sparkles,
  Plug,
  PlugZap,
  Euro,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";

interface Platform {
  id: string;
  name: string;
  logo: string;
  type: string;
  official_url: string;
  requires: string[];
  description: string;
  connected: boolean;
  last_check_at?: string;
}

interface Dossier {
  id: string;
  reference: string;
  title: string;
  buyer?: string;
  description?: string;
  deadline?: string;
  budget_eur_estimate?: number;
  cpv?: string;
  geo_zone?: string;
  source_platform?: string;
  source_url?: string;
  status: string;
  draft?: {
    memoire_technique: string;
    proposition_commerciale_summary: string;
    questions_a_poser_avant_envoi: string[];
    generated_at: string;
  };
  interactions: Array<{ id: string; type: string; message: string; at: string }>;
  created_at: string;
  updated_at: string;
}

interface Kpis {
  total: number;
  detected: number;
  drafting: number;
  submitted: number;
  won: number;
  lost: number;
  potential_eur: number;
}

interface SearchProfile {
  cpv_codes: string[];
  naf_codes: string[];
  keywords: string[];
  geo_zones: string[];
  budget_min_eur: number;
  budget_max_eur: number;
  excluded_keywords: string[];
  notes: string;
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  detected: { label: "Détecté", color: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  studying: { label: "À l'étude", color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  drafting: { label: "Réponse en cours", color: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
  ready: { label: "Prêt à envoyer", color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
  submitted: { label: "Soumis", color: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
  won: { label: "Gagné", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  lost: { label: "Perdu", color: "bg-destructive/15 text-destructive border-destructive/30" },
};

export default function TendersDashboard() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const [profile, setProfile] = useState<SearchProfile>({
    cpv_codes: [],
    naf_codes: [],
    keywords: [],
    geo_zones: [],
    budget_min_eur: 0,
    budget_max_eur: 0,
    excluded_keywords: [],
    notes: "",
  });
  const [keywordsText, setKeywordsText] = useState("");
  const [excludedText, setExcludedText] = useState("");
  const [geoText, setGeoText] = useState("");
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [busyDossierId, setBusyDossierId] = useState<string | null>(null);
  const [openDraft, setOpenDraft] = useState<Dossier | null>(null);
  const [connectPlatform, setConnectPlatform] = useState<Platform | null>(null);
  const [creds, setCreds] = useState<{ api_key?: string; login?: string; password?: string }>({});

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, pf, d] = await Promise.all([
        fetch(`${BACKEND}/api/tenders/profile`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${BACKEND}/api/tenders/platforms`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${BACKEND}/api/tenders/dossiers`, { credentials: "include" }).then((r) => r.json()),
      ]);
      setProfile({
        cpv_codes: p.cpv_codes || [],
        naf_codes: p.naf_codes || [],
        keywords: p.keywords || [],
        geo_zones: p.geo_zones || [],
        budget_min_eur: p.budget_min_eur || 0,
        budget_max_eur: p.budget_max_eur || 0,
        excluded_keywords: p.excluded_keywords || [],
        notes: p.notes || "",
      });
      setKeywordsText((p.keywords || []).join(", "));
      setExcludedText((p.excluded_keywords || []).join(", "));
      setGeoText((p.geo_zones || []).join(", "));
      setPlatforms(pf.platforms || []);
      setDossiers(d.dossiers || []);
      setKpis(d.kpis || null);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    const payload: SearchProfile = {
      ...profile,
      keywords: keywordsText.split(",").map((s) => s.trim()).filter(Boolean),
      excluded_keywords: excludedText.split(",").map((s) => s.trim()).filter(Boolean),
      geo_zones: geoText.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      const r = await fetch(`${BACKEND}/api/tenders/profile`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error("save failed");
      setProfile(payload);
      toast.success("Profil de recherche enregistré");
    } catch {
      toast.error("Impossible d'enregistrer");
    } finally {
      setSavingProfile(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const r = await fetch(`${BACKEND}/api/tenders/analyze`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Analyse impossible");
      }
      const d = await r.json();
      toast.success(
        `${d.created} nouveau(x) appel(s) d'offres détecté(s) (${d.found} trouvés${d.demo_mode ? " — mode démo" : ""})`,
      );
      await loadAll();
    } catch (e: any) {
      toast.error(e.message || "Erreur d'analyse");
    } finally {
      setAnalyzing(false);
    }
  };

  const connect = async () => {
    if (!connectPlatform) return;
    try {
      const r = await fetch(`${BACKEND}/api/tenders/platforms/${connectPlatform.id}/connect`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(creds),
      });
      if (!r.ok) throw new Error("connexion échouée");
      toast.success(`${connectPlatform.name} connecté`);
      setConnectPlatform(null);
      setCreds({});
      loadAll();
    } catch {
      toast.error("Échec de connexion");
    }
  };

  const disconnect = async (pid: string) => {
    try {
      await fetch(`${BACKEND}/api/tenders/platforms/${pid}`, {
        method: "DELETE",
        credentials: "include",
      });
      toast.success("Plateforme déconnectée");
      loadAll();
    } catch {
      toast.error("Erreur");
    }
  };

  const updateDossierStatus = async (d: Dossier, status: string) => {
    try {
      const r = await fetch(`${BACKEND}/api/tenders/dossiers/${d.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error();
      toast.success("Statut mis à jour");
      loadAll();
    } catch {
      toast.error("Erreur");
    }
  };

  const generateResponse = async (d: Dossier) => {
    setBusyDossierId(d.id);
    try {
      const r = await fetch(`${BACKEND}/api/tenders/dossiers/${d.id}/generate-response`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("génération impossible");
      const j = await r.json();
      toast.success("Réponse générée par Grafton IA");
      const updated = { ...d, draft: j.draft, status: "drafting" };
      setOpenDraft(updated);
      loadAll();
    } catch {
      toast.error("Erreur de génération");
    } finally {
      setBusyDossierId(null);
    }
  };

  const submitDossier = async (d: Dossier) => {
    if (!confirm(`Confirmer la soumission de "${d.title}" ?\n\nGrafton la marquera comme soumise et créera une interaction.`)) return;
    setBusyDossierId(d.id);
    try {
      const r = await fetch(`${BACKEND}/api/tenders/dossiers/${d.id}/submit`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmation: true, submission_note: "" }),
      });
      if (!r.ok) throw new Error();
      toast.success("Dossier soumis");
      loadAll();
    } catch {
      toast.error("Erreur");
    } finally {
      setBusyDossierId(null);
    }
  };

  const deleteDossier = async (d: Dossier) => {
    if (!confirm(`Supprimer définitivement "${d.title}" ?`)) return;
    try {
      await fetch(`${BACKEND}/api/tenders/dossiers/${d.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      toast.success("Supprimé");
      loadAll();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 p-6 md:p-10 overflow-y-auto">
          <div className="max-w-6xl mx-auto space-y-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight" data-testid="tenders-title">
                Marchés publics
              </h1>
              <p className="text-muted-foreground mt-1">
                Détection automatique des appels d'offres, génération de réponses par Grafton IA et suivi des dossiers.
              </p>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card data-testid="tender-kpi-total">
                <CardContent className="pt-5">
                  <div className="text-2xl font-bold">{kpis?.total ?? 0}</div>
                  <div className="text-xs text-muted-foreground">Dossiers</div>
                </CardContent>
              </Card>
              <Card data-testid="tender-kpi-detected">
                <CardContent className="pt-5">
                  <div className="text-2xl font-bold text-sky-300">{kpis?.detected ?? 0}</div>
                  <div className="text-xs text-muted-foreground">Détectés</div>
                </CardContent>
              </Card>
              <Card data-testid="tender-kpi-drafting">
                <CardContent className="pt-5">
                  <div className="text-2xl font-bold text-violet-300">{kpis?.drafting ?? 0}</div>
                  <div className="text-xs text-muted-foreground">En rédaction</div>
                </CardContent>
              </Card>
              <Card data-testid="tender-kpi-submitted">
                <CardContent className="pt-5">
                  <div className="text-2xl font-bold text-orange-300">{kpis?.submitted ?? 0}</div>
                  <div className="text-xs text-muted-foreground">Soumis</div>
                </CardContent>
              </Card>
              <Card data-testid="tender-kpi-potential">
                <CardContent className="pt-5">
                  <div className="text-2xl font-bold text-emerald-300">
                    {((kpis?.potential_eur ?? 0) / 1000).toFixed(0)}k€
                  </div>
                  <div className="text-xs text-muted-foreground">Potentiel cumulé</div>
                </CardContent>
              </Card>
            </div>

            {/* Profile */}
            <Card data-testid="tenders-profile">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Search className="h-5 w-5 text-primary" />
                  Profil de recherche
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="keywords">Mots-clés (séparés par virgules)</Label>
                  <Input
                    id="keywords"
                    value={keywordsText}
                    onChange={(e) => setKeywordsText(e.target.value)}
                    placeholder="intelligence artificielle, automatisation, conseil SI"
                    data-testid="tenders-keywords-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="geo">Zones géographiques (codes département ou régions)</Label>
                    <Input
                      id="geo"
                      value={geoText}
                      onChange={(e) => setGeoText(e.target.value)}
                      placeholder="75, 92, 69"
                      data-testid="tenders-geo-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="excluded">Mots-clés exclus</Label>
                    <Input
                      id="excluded"
                      value={excludedText}
                      onChange={(e) => setExcludedText(e.target.value)}
                      placeholder="travaux, BTP"
                      data-testid="tenders-excluded-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bmin">Budget min (€)</Label>
                    <Input
                      id="bmin"
                      type="number"
                      value={profile.budget_min_eur}
                      onChange={(e) =>
                        setProfile({ ...profile, budget_min_eur: parseInt(e.target.value || "0") })
                      }
                      data-testid="tenders-budget-min"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bmax">Budget max (€)</Label>
                    <Input
                      id="bmax"
                      type="number"
                      value={profile.budget_max_eur}
                      onChange={(e) =>
                        setProfile({ ...profile, budget_max_eur: parseInt(e.target.value || "0") })
                      }
                      data-testid="tenders-budget-max"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes">Notes stratégiques</Label>
                  <Textarea
                    id="notes"
                    value={profile.notes}
                    onChange={(e) => setProfile({ ...profile, notes: e.target.value })}
                    rows={2}
                    placeholder="Spécificités d'expertise, certifications, références notables…"
                    data-testid="tenders-notes-input"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={saveProfile}
                    disabled={savingProfile}
                    data-testid="tenders-save-profile"
                  >
                    {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Enregistrer le profil
                  </Button>
                  <Button
                    variant="default"
                    onClick={runAnalysis}
                    disabled={analyzing || !keywordsText.trim()}
                    data-testid="tenders-run-analysis"
                    className="gap-2"
                  >
                    {analyzing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    Lancer la détection
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Platforms */}
            <Card data-testid="tenders-platforms">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <PlugZap className="h-5 w-5 text-primary" />
                  Plateformes connectées
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {platforms.map((p) => (
                    <div
                      key={p.id}
                      className="border border-border/60 rounded-lg p-3 flex items-start gap-3"
                      data-testid={`tender-platform-${p.id}`}
                    >
                      <div className="text-2xl">{p.logo}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{p.name}</span>
                          {p.connected ? (
                            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                              Connecté
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              Non connecté
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{p.type}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                          {p.description}
                        </p>
                        <div className="flex gap-2 mt-2">
                          {p.connected ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => disconnect(p.id)}
                              data-testid={`tender-disconnect-${p.id}`}
                            >
                              Déconnecter
                            </Button>
                          ) : (
                            p.requires.length > 0 && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => {
                                  setConnectPlatform(p);
                                  setCreds({});
                                }}
                                data-testid={`tender-connect-${p.id}`}
                              >
                                <Plug className="h-3.5 w-3.5 mr-1" /> Connecter
                              </Button>
                            )
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            asChild
                            data-testid={`tender-site-${p.id}`}
                          >
                            <a href={p.official_url} target="_blank" rel="noreferrer">
                              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Site
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Note : BOAMP (source officielle France) est interrogé automatiquement, sans clé.
                </p>
              </CardContent>
            </Card>

            {/* Dossiers list */}
            <Card data-testid="tenders-dossiers">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5 text-primary" />
                  Dossiers détectés ({dossiers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-10 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
                  </div>
                ) : dossiers.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground space-y-2">
                    <Search className="h-8 w-8 mx-auto opacity-50" />
                    <div>Aucun dossier pour le moment.</div>
                    <div className="text-xs">Définissez vos mots-clés et lancez la détection.</div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dossiers.map((d) => {
                      const meta = STATUS_META[d.status] || STATUS_META.detected;
                      return (
                        <div
                          key={d.id}
                          className="border border-border/60 rounded-lg p-4 space-y-2"
                          data-testid={`tender-dossier-${d.id}`}
                        >
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold">{d.title}</span>
                                <Badge variant="outline" className={meta.color}>
                                  {meta.label}
                                </Badge>
                                {d.source_platform && (
                                  <Badge variant="outline" className="text-muted-foreground">
                                    {d.source_platform}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {d.reference} · {d.buyer}
                              </div>
                            </div>
                            <div className="text-right text-xs text-muted-foreground space-y-0.5">
                              {d.deadline && (
                                <div className="flex items-center justify-end gap-1">
                                  <Clock className="h-3 w-3" /> {d.deadline}
                                </div>
                              )}
                              {!!d.budget_eur_estimate && (
                                <div className="flex items-center justify-end gap-1">
                                  <Euro className="h-3 w-3" />
                                  {(d.budget_eur_estimate / 1000).toFixed(0)}k€
                                </div>
                              )}
                              {d.geo_zone && (
                                <div className="flex items-center justify-end gap-1">
                                  <MapPin className="h-3 w-3" /> {d.geo_zone}
                                </div>
                              )}
                            </div>
                          </div>

                          {d.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">{d.description}</p>
                          )}

                          <div className="flex flex-wrap gap-2 pt-1">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => generateResponse(d)}
                              disabled={busyDossierId === d.id}
                              data-testid={`tender-generate-${d.id}`}
                              className="gap-1.5"
                            >
                              {busyDossierId === d.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Sparkles className="h-3.5 w-3.5" />
                              )}
                              {d.draft ? "Voir la réponse" : "Générer réponse (Grafton IA)"}
                            </Button>
                            {d.draft && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setOpenDraft(d)}
                                data-testid={`tender-show-draft-${d.id}`}
                              >
                                <FileText className="h-3.5 w-3.5 mr-1" /> Brouillon
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateDossierStatus(d, "studying")}
                              disabled={d.status === "studying"}
                              data-testid={`tender-study-${d.id}`}
                            >
                              À l'étude
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => submitDossier(d)}
                              disabled={busyDossierId === d.id || d.status === "submitted"}
                              data-testid={`tender-submit-${d.id}`}
                              className="gap-1.5"
                            >
                              <Send className="h-3.5 w-3.5" /> Soumettre
                            </Button>
                            {d.source_url && (
                              <Button
                                size="sm"
                                variant="ghost"
                                asChild
                                data-testid={`tender-source-${d.id}`}
                              >
                                <a href={d.source_url} target="_blank" rel="noreferrer">
                                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Annonce
                                </a>
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => updateDossierStatus(d, "won")}
                              data-testid={`tender-won-${d.id}`}
                              className="text-emerald-400"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Gagné
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteDossier(d)}
                              data-testid={`tender-delete-${d.id}`}
                              className="text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      {/* Connect platform dialog */}
      <Dialog open={!!connectPlatform} onOpenChange={() => setConnectPlatform(null)}>
        <DialogContent className="max-w-md" data-testid="connect-platform-dialog">
          {connectPlatform && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="text-2xl">{connectPlatform.logo}</span>
                  Connecter {connectPlatform.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <p className="text-sm text-muted-foreground">{connectPlatform.description}</p>
                {connectPlatform.requires.includes("api_key") && (
                  <div>
                    <Label htmlFor="api_key">Clé API</Label>
                    <Input
                      id="api_key"
                      type="password"
                      value={creds.api_key || ""}
                      onChange={(e) => setCreds({ ...creds, api_key: e.target.value })}
                      placeholder="••••••••••••"
                      data-testid="platform-api-key-input"
                    />
                  </div>
                )}
                {connectPlatform.requires.includes("login") && (
                  <div>
                    <Label htmlFor="login">Identifiant</Label>
                    <Input
                      id="login"
                      value={creds.login || ""}
                      onChange={(e) => setCreds({ ...creds, login: e.target.value })}
                      data-testid="platform-login-input"
                    />
                  </div>
                )}
                {connectPlatform.requires.includes("password") && (
                  <div>
                    <Label htmlFor="password">Mot de passe</Label>
                    <Input
                      id="password"
                      type="password"
                      value={creds.password || ""}
                      onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                      data-testid="platform-password-input"
                    />
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Vos credentials sont stockés chiffrés et utilisés uniquement pour interroger l'API officielle.
                </p>
                <Button
                  onClick={connect}
                  className="w-full"
                  data-testid="platform-connect-submit"
                >
                  Connecter
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Draft dialog */}
      <Dialog open={!!openDraft} onOpenChange={() => setOpenDraft(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="draft-dialog">
          {openDraft && openDraft.draft && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Réponse — {openDraft.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Mémoire technique</div>
                  <div className="bg-muted/30 rounded p-3 text-sm whitespace-pre-wrap">
                    {openDraft.draft.memoire_technique}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Proposition commerciale</div>
                  <div className="bg-muted/30 rounded p-3 text-sm whitespace-pre-wrap">
                    {openDraft.draft.proposition_commerciale_summary}
                  </div>
                </div>
                {openDraft.draft.questions_a_poser_avant_envoi?.length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">
                      Questions à valider avant envoi
                    </div>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      {openDraft.draft.questions_a_poser_avant_envoi.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${openDraft.draft?.memoire_technique}\n\n---\n\n${openDraft.draft?.proposition_commerciale_summary}`,
                      );
                      toast.success("Copié");
                    }}
                    variant="outline"
                    data-testid="draft-copy"
                  >
                    Copier
                  </Button>
                  <Button
                    onClick={() => submitDossier(openDraft)}
                    data-testid="draft-submit"
                    className="gap-1.5"
                  >
                    <Send className="h-4 w-4" /> Marquer comme soumis
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
